import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ref as storageRef, getDownloadURL } from 'firebase/storage';
import { storage } from '../../firebase';
import { getAllChats, type ChatData } from '../../services/admin';
import { useToast } from '../../hooks/useToast';
import ConversationView from '../../components/ConversationView';
import LessonVideoPreview from '../../components/LessonVideoPreview';
import { LessonEditPreview } from '../../components/LessonEditPreview';
import type { Lesson } from '../../types/lesson';
import type { Order } from '../../types/order';
import { getOrder } from '../../services/orders';
import { formatOfferingLabel } from '../../utils/offeringLabel';

export default function AdminChats() {
  const [chats, setChats] = useState<ChatData[]>([]);
  const [selectedChat, setSelectedChat] = useState<ChatData | null>(null);
  const [loading, setLoading] = useState(true);
  const [searchParams] = useSearchParams();
  const { showError } = useToast();

  // Lesson video modal — opened when admin clicks "Watch Lesson" in chat
  const [lessonModal, setLessonModal] = useState<Lesson | null>(null);
  const [lessonDownloadUrls, setLessonDownloadUrls] = useState<{ player?: string; coach?: string }>({});
  /* The lesson doc has no offering type or total, and its coachName is only set
     on transfer, so the player's title needs the parent order. */
  const [lessonModalOrder, setLessonModalOrder] = useState<Order | null>(null);

  useEffect(() => {
    loadChats();
  }, []);

  useEffect(() => {
    const conversationId = searchParams.get('conversation');
    if (conversationId && chats.length > 0) {
      const chat = chats.find(c => c.id === conversationId);
      if (chat) setSelectedChat(chat);
    }
  }, [searchParams, chats]);

  useEffect(() => {
    setLessonModalOrder(null);
    if (!lessonModal?.orderId) return;
    let cancelled = false;
    getOrder(lessonModal.orderId)
      .then((o) => { if (!cancelled) setLessonModalOrder(o); })
      .catch((err) => { console.warn('AdminChats: could not load order for lesson title', err); });
    return () => { cancelled = true; };
  }, [lessonModal]);

  // Resolve fresh download URLs from storage paths whenever the lesson modal opens.
  // URLs stored in Firestore are 7-day signed URLs that expire; getDownloadURL
  // returns a non-expiring Firebase token URL instead.
  useEffect(() => {
    if (!lessonModal) { setLessonDownloadUrls({}); return; }
    let cancelled = false;
    (async () => {
      const urls: { player?: string; coach?: string } = {};
      if (lessonModal.playerVideoStoragePath) {
        try { urls.player = await getDownloadURL(storageRef(storage, lessonModal.playerVideoStoragePath)); }
        catch { urls.player = lessonModal.playerVideoUrl; }
      } else {
        urls.player = lessonModal.playerVideoUrl;
      }
      if (lessonModal.coachVideoStoragePath) {
        try { urls.coach = await getDownloadURL(storageRef(storage, lessonModal.coachVideoStoragePath)); }
        catch { urls.coach = lessonModal.coachVideoUrl; }
      } else {
        urls.coach = lessonModal.coachVideoUrl;
      }
      if (!cancelled) setLessonDownloadUrls(urls);
    })();
    return () => { cancelled = true; };
  }, [lessonModal]);

  async function loadChats() {
    try {
      setLoading(true);
      const data = await getAllChats();
      setChats(data);
    } catch (error) {
      console.error('Error loading chats:', error);
      showError('Failed to load chats');
    } finally {
      setLoading(false);
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  function formatTimestamp(timestamp: any) {
    if (!timestamp) return '';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleString();
  }

  if (loading) {
    return (
      <div className="p-8">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/4"></div>
          <div className="h-96 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col">
      {/* Header */}
      <div className="p-8 pb-4">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Chats</h1>
        <p className="text-gray-600 mb-4">View and moderate all chat threads</p>
      </div>

      {/* Main content area */}
      <div className="flex-1 flex overflow-hidden px-8 pb-8">
        {/* Conversations list */}
        <div className="w-1/3 bg-white rounded-lg shadow overflow-hidden flex flex-col">
          <div className="p-4 border-b border-gray-200">
            <h3 className="font-semibold text-gray-900">Conversations ({chats.length})</h3>
          </div>
          <div className="flex-1 overflow-y-auto">
            {chats.map((chat) => (
              <button
                key={chat.id}
                onClick={() => setSelectedChat(chat)}
                className={`w-full text-left p-4 border-b border-gray-200 hover:bg-gray-50 transition ${
                  selectedChat?.id === chat.id ? 'bg-blue-50' : ''
                }`}
              >
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {Object.values(chat.participantDetails || {})
                      .map(p => p.name)
                      .join(' & ')}
                  </p>
                  <p className="text-xs text-gray-500 truncate mt-0.5">
                    {chat.lastMessage || 'No messages yet'}
                  </p>
                  {chat.lastMessageAt && (
                    <p className="text-xs text-gray-400 mt-0.5">
                      {formatTimestamp(chat.lastMessageAt)}
                    </p>
                  )}
                </div>
              </button>
            ))}
            {chats.length === 0 && (
              <div className="p-8 text-center text-gray-500">No conversations found</div>
            )}
          </div>
        </div>

        {/* Conversation panel — uses ConversationView so admin gets edit/delete */}
        <div className="flex-1 ml-4 bg-white rounded-lg shadow overflow-hidden flex flex-col">
          {selectedChat ? (
            <ConversationView
              key={selectedChat.id}
              conversationId={selectedChat.id}
              hideHeader={false}
              readOnly
              onOpenLesson={setLessonModal}
            />
          ) : (
            <div className="flex items-center justify-center h-full text-gray-500">
              Select a conversation to view messages
            </div>
          )}
        </div>
      </div>

      {/* Lesson video modal */}
      {lessonModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col">
            <div className="px-6 py-5 border-b border-gray-100 flex items-start justify-between flex-shrink-0">
              <div>
                <h2 className="text-lg font-bold text-gray-900">Lesson Videos</h2>
                <p className="text-sm text-gray-500 mt-0.5">
                  {lessonModal.coachName ?? 'Coach'}
                  {lessonModal.lessonNumber != null && ` · Lesson ${lessonModal.lessonNumber}`}
                </p>
              </div>
              <button onClick={() => setLessonModal(null)} className="text-gray-400 hover:text-gray-600 text-xl leading-none">×</button>
            </div>
            <div className="overflow-y-auto px-6 py-5 space-y-6">
              {/* Player video */}
              {lessonModal.playerVideoUrl ? (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Player Video</h3>
                    {lessonDownloadUrls.player ? (
                      <a href={lessonDownloadUrls.player} target="_blank" rel="noopener noreferrer"
                        className="flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-800">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
                        </svg>
                        Download
                      </a>
                    ) : <span className="text-xs text-gray-400">Resolving…</span>}
                  </div>
                  <LessonVideoPreview videoUrl={lessonModal.playerVideoUrl} maxHeight="300px" />
                </div>
              ) : (
                <div>
                  <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Player Video</h3>
                  <p className="text-sm text-gray-400 italic">No player video uploaded yet.</p>
                </div>
              )}

              {/* Coach response */}
              {lessonModal.publishedOriginalVideoId && lessonModal.publishedDraftId ? (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Coach Response</h3>
                    {lessonDownloadUrls.coach ? (
                      <a href={lessonDownloadUrls.coach} target="_blank" rel="noopener noreferrer"
                        className="flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-800">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
                        </svg>
                        Download
                      </a>
                    ) : lessonModal.coachVideoUrl ? (
                      <span className="text-xs text-gray-400">Resolving…</span>
                    ) : null}
                  </div>
                  <LessonEditPreview
                    context={{ type: 'lesson', id: lessonModal.id }}
                    originalVideoId={lessonModal.publishedOriginalVideoId}
                    draftId={lessonModal.publishedDraftId}
                    notes={lessonModal.coachNotes}
                    coachName={lessonModal.coachName ?? lessonModalOrder?.coachName}
                    offeringLabel={lessonModalOrder ? formatOfferingLabel({ offeringType: lessonModalOrder.type, lessonNumber: lessonModal.lessonNumber, totalLessons: lessonModalOrder.totalLessons }) : undefined}
                    deliveredAt={lessonModal.deliveredAt}
                    showControls={true}
                    downloadUrl={lessonDownloadUrls.coach}
                  />
                </div>
              ) : lessonModal.coachNotes ? (
                <div>
                  <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Coach Notes</h3>
                  <p className="text-sm text-gray-700 whitespace-pre-wrap">{lessonModal.coachNotes}</p>
                </div>
              ) : null}
            </div>
            <div className="px-6 py-4 border-t border-gray-100 flex justify-end flex-shrink-0">
              <button
                onClick={() => setLessonModal(null)}
                className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 rounded-lg transition"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
